import {useSession} from "next-auth/react";
import AuthLayout from "../../core-web/components/core/templates/AuthLayout";
import {useProfile} from "../../core-web/data/profile";
import {NotionConnectionCard} from "../../core-web/components/core/organisms/NotionConnectionCard";
import {EmailVerificationCard} from "../../core-web/components/core/organisms/EmailVerificationCard";
import {DatabaseCard} from "../../core-web/components/core/organisms/DatabaseCardV2";
import Link from "next/link";
import {ProfileInfoBar2} from "../../core-web/components/core/organisms/ProfileInfoBar";
import {PageHead} from "../../core-web/components/core/templates/PageHead";
import {AppLayout} from "../../core-web/components/core/templates/AppLayout";
import {
  cancelSubscriptionApi, connectSESDomainAPI,
  connectSESEmailAPI,
  disconnectNotionAPI, disconnectSESDomainAPI,
  disconnectSESEmailAPI
} from "../../core-web/services/api";
import {LANDING_META} from "../../core-web/constants";
import {Box, Button, CircularProgress, Flex, Heading, HStack, Input, Stack, Text} from "@chakra-ui/react";
import {useStatusToast} from "../../core-web/hooks/useStatusToast";
import React, {useEffect, useState} from "react";
import {formatKeyToEmail} from "@app/core-app/app/domain/email";
import {event, identify, setAmplitudeUser} from "../../core-web/services/analytics";
import debounce from 'lodash/debounce';
import {CreateFormButton} from "../../core-web/components/core/organisms/CreateFormModal";
import {GetHelpButton} from "../../core-web/components/core/organisms/GetHelpModal";
import {publicEnv} from "@app/core-app/pub-env";
import {VerifiedDomainCard} from "../../core-web/components/core/organisms/DomainVerificationCard";
import {RefreshIcon} from "@heroicons/react/outline";
import {DropdownSelector} from "../../core-web/components/core/atoms/DropdownSelector";
import UserIntro from "../../core-web/components/core/organisms/UserIntro";
import UpgradePlanButton from "../../core-web/components/core/organisms/UpgradePlanModal";

const Home = () => {

  const {data, isLoading, isValidating, mutate: mutateProfile} = useProfile()
  const {data: sessionData} = useSession()
  const [search, setSearch] = useState('')
  const {addToast, updateToastResult, toastCatch} = useStatusToast()
  const [showMax, setShowMax] = useState(10)


  const params = `?checkout[email]=${data?.profile?.email}&checkout[custom][ns_email]=${data?.profile?.email}`
  const monthlyPremiumUpgradeLink = publicEnv.PREMIUM_URL + params
  const annualPremiumUpgradeLink = publicEnv.ANNUAL_PREMIUM_URL + params


  useEffect(() => {
    // @ts-ignore
    if (window?.createLemonSqueezy) {
      // @ts-ignore
      window.createLemonSqueezy();
    }
  }, [])
  useEffect(() => {
    if (data?.profile._id) {
      setAmplitudeUser(data.profile._id?.toString())
      identify({
        addressesInUse: data.usageData.addressesInUse,
        sentEmails: data.usageData.sentEmails,
        savedEmails: data.usageData.savedEmails,
        autosendEnabledCount: data.usageData.autosendEnabledCount,
        email: data.profile.email,
        notionConnected: !!data.profile.notionConnection?.accessToken,
        senderEmailVerified: data.emailVerified
      })
    }
  }, [data])


  const onCancelClick = ({reason, comment}: { reason: string, comment: string }) => {
    event({
      action: "cancel_subscription",
      params: {
        reason: reason,
        comment: comment,
      }
    })
    addToast({title: "Canceling 😢"})
    return cancelSubscriptionApi()
      .then(r => updateToastResult(r, {title: "Subscription will be canceled at the end of billing cycle"}))
      .then(() => mutateProfile())
      .catch(toastCatch)
  }

  const filteredDatabases = data?.databases
    ?.filter(d => {
      const nameSearch = d.name?.toLowerCase()?.includes(search?.toLowerCase())
      let key = data.profile?.dbConfigurations?.find(db => db.databaseId === d.id)?.key
      if (key) {
        key = formatKeyToEmail(key)
      }

      const keySearch = key?.includes(search?.toLowerCase()) || false
      return nameSearch || keySearch
    })

  let displayedDatabases = filteredDatabases;

  const filterOptions = ["Show all Notion databases", "Show Inboxes with address", "Show configured outboxes"]
  const [filter, setFilter] = useState(filterOptions[0]);
  if (filter === filterOptions[1]) {
    displayedDatabases = filteredDatabases.filter(db => {
      const config = data.profile?.dbConfigurations?.find(config => config.databaseId === db.id);
      return config?.key;
    });
  } else if (filter === filterOptions[2]) {
    displayedDatabases = filteredDatabases.filter(db => {
      const config = data.profile?.dbConfigurations?.find(config => config.databaseId === db.id);
      return Object.keys(config?.outbox || {}).length > 0 && db.outboxConfig.report.result === "ok";
    });
  }


  // @ts-ignore
  return <PageHead title={"Home | NotionSender"} description={LANDING_META}>
    <AuthLayout>
      <AppLayout>
        <div>
          <div className={""}>
            {sessionData?.user &&
                          <ProfileInfoBar2 session={sessionData} data={data} onCancelClick={onCancelClick}>
                            {isLoading && <div>Loading...</div>}

                            {data && <Stack
                              direction={["column", "column", "row"]}
                              alignItems={"start"} py={2}
                              spacing={{base: 4, md: 2}}
                              flexWrap={"wrap"}>

                              {data.billingData?.status?.toLowerCase() !== "active" &&
                                  <UpgradePlanButton monthlyPlanLink={monthlyPremiumUpgradeLink} yearlyPlanLink={annualPremiumUpgradeLink}/>}

                              {data.profile.notionConnection && <CreateFormButton/>}
                              <Button
                                w={{base: "100%", md: "auto"}}
                                variant={"outline"} size={"sm"} colorScheme={"gray"}>
                                <Link href={"/create"} legacyBehavior>
                                  <a>Create email from Notion page</a>
                                </Link>
                              </Button>

                              {data.profile.billingInfo?.subscriptionId &&
                                    <Button
                                      w={{base: "100%", md: "auto"}}
                                      variant={"outline"} size={"sm"} colorScheme={"gray"}
                                      onClick={() => {
                                        event({action: "billing_portal_click", params: {}})
                                        window.open("https://notionsender.lemonsqueezy.com/billing", '_blank').focus();
                                      }}
                                    >
                                      <span>Open Billing Portal</span>
                                    </Button>
                              }
                              <Stack w={{base: "100%", md: "auto"}}
                                direction={"row"}
                                alignItems={"start"}
                                spacing={{base: 4, md: 2}}
                              >
                                <Button variant={"outline"}
                                  size={"sm"}
                                  w={{base: "100%", md: "auto"}}
                                  colorScheme={"gray"}
                                  onClick={() => {
                                    event({action: "share_feedback", params: {}})
                                  }}>
                                  <a href={"mailto:notionsender@gmail.com"}>Share feedback</a>
                                </Button>
                                <GetHelpButton/>
                              </Stack>
                            </Stack>}

                          </ProfileInfoBar2>
            }


            {data && !data?.profile?.notionConnection && <UserIntro/>}
            {data &&
                          <div className={""}>
                            <Heading size={"lg"} fontWeight={"medium"} marginY={"8"}>Connections</Heading>
                            <Box marginY={"4"}>
                              <NotionConnectionCard
                                notionConnection={data?.profile?.notionConnection}
                                onDisconnect={() => {
                                  addToast({title: "Disconnecting Notion"})
                                  return disconnectNotionAPI()
                                    .then(r => updateToastResult(r, {title: "Disconnected 🎉"}))
                                    .then(() => mutateProfile())
                                    .catch(toastCatch)
                                }}/>
                            </Box>


                            <Box marginY={"4"}>
                              <VerifiedDomainCard
                                formActive={!!data.profile.premium}
                                domainEntity={data?.verifiedDomain}
                                domainVerification={data?.profile?.domainVerification}
                                onConnectDomain={(domain) => {
                                  addToast({title: "Creating domain verification"})
                                  return connectSESDomainAPI({domain})
                                    .then(r => updateToastResult(r, {title: "Please check verification details"}))
                                    .then(() => mutateProfile())
                                    .catch(toastCatch)
                                }}

                                onDisconnectDomain={() => {
                                  addToast({title: "Disconnecting domain"})
                                  return disconnectSESDomainAPI()
                                    .then(() => mutateProfile())
                                    .then(r => updateToastResult(r, {title: "Domain removed 🎉"}))
                                    .catch(toastCatch)

                                }}

                                onRefreshStatus={() => {
                                  const domain = data?.verifiedDomain?.domain
                                  if (domain) {
                                    addToast({title: "Refreshing status"})
                                    return connectSESDomainAPI({domain: domain, sync: true})
                                      .then(() => mutateProfile())
                                      .then(r => updateToastResult(r, {title: "Updated"}))
                                      .catch(toastCatch)
                                  }
                                }}
                              />
                            </Box>

                            <Box marginY={"4"}>
                              <EmailVerificationCard
                                formActive={!!data.profile.premium}
                                emailIsVerified={data?.emailVerified}
                                userEmail={data?.profile?.email}
                                verifiedEmail={data?.profile?.emailVerification?.email}
                                onConnect={(email) => {
                                  addToast({title: "Sending verification email"})
                                  return connectSESEmailAPI(email)
                                    .then(r => updateToastResult(r, {title: "Verification email sent 🎉"}))
                                    .then(() => mutateProfile())
                                    .catch(toastCatch)
                                }}

                                onDisconnect={() => {
                                  addToast({title: "Disconnecting email"})
                                  return disconnectSESEmailAPI()
                                    .then(r => updateToastResult(r, {title: "Email removed 🎉"}))
                                    .then(() => mutateProfile())
                                    .catch(toastCatch)

                                }}
                              />
                            </Box>


                            <div className={""}>
                              <HStack alignItems={"center"} mt={8} mb={2}>
                                <Heading size={"lg"} fontWeight={"medium"}>Notion Databases</Heading>
                                {isValidating && <CircularProgress size={5} isIndeterminate color='blue.500'/>}
                                {!isValidating &&
                                    <Box color="gray.600"
                                      onClick={() => {
                                        if (mutateProfile) {
                                          event({action: "refresh_click", params: {}})
                                          mutateProfile()
                                        }
                                      }} h={5} w={5}>
                                      <RefreshIcon strokeWidth={1.25}/>
                                    </Box>}
                              </HStack>

                              <Text fontSize={"sm"}>Share your Notion database with NotionSender app to configure
                                them.</Text>
                              <Text fontSize={"sm"}>It may take a minute for recently created database to appear in the
                                list.</Text>

                              <Stack w={'full'} direction={{base: "column", md: "row"}}>
                                {data?.databases?.length > 0 &&
                                  <Box  w='full' maxW={"md"} my={4}>
                                    <Text mb={1} fontSize={"sm"}>Search:</Text>
                                    <Input placeholder='Search by name or address' bgColor={"white"}
                                      onChange={debounce((e) => {
                                        event({action: "search_database", params: {q: e.target.value}})
                                        setSearch(e.target.value)
                                      }, 500)}/>

                                  </Box>
                                }
                                {data?.databases?.length > 0 &&
                                  <Box w='full' maxW={"md"} my={4}>
                                    <Text mb={1} fontSize={"sm"}>Quick filters:</Text>
                                    <DropdownSelector
                                      value={filter}
                                      options={filterOptions}
                                      onChange={(dataObject) => {
                                        event({
                                          action: "filter_database",
                                          params: {filter: dataObject?.value}
                                        })
                                        setFilter(dataObject?.value)
                                      }
                                      }
                                    />

                                  </Box>}
                              </Stack>

                              <div className={"space-y-12"}>
                                {displayedDatabases
                                  ?.slice(0, showMax)
                                  ?.map((database, index) => {
                                    return <DatabaseCard key={database.id} database={database} profile={data}/>
                                  })}
                              </div>
                              {displayedDatabases?.length > showMax &&
                                  <Flex w={"full"} py={8} mb={8}>
                                    <Button variant={"outline"}
                                      size={"md"}
                                      colorScheme={"blue"}
                                      onClick={() => setShowMax(showMax + 20)}>
                                      <span>Show more</span>
                                    </Button>
                                  </Flex>
                              }

                              {displayedDatabases?.length <= showMax &&
                                  <div className={"text-gray-500 text-sm flex items-center justify-center pt-8"}>
                                    <span className={""}>No databases? Make sure you granted NotionSender app access to the Notion database</span>

                                  </div>
                              }
                            </div>


                          </div>
            }
          </div>
        </div>
      </AppLayout>
    </AuthLayout>
  </PageHead>

}

export default Home
